import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Building2, Plus, LogIn, LogOut, Printer, Users, Trash2, Loader2, Database, HardDrive } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Format bytes -> human readable (1.23 MB, 456 KB)
const fmtBytes = (b) => {
  if (!b || b < 0) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(2)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const CompanyManagerPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ company_name: '', username: '', password: '', user_password: '' });
  const [creating, setCreating] = useState(false);
  const [dbStats, setDbStats] = useState(null);
  const [quotaInput, setQuotaInput] = useState('');
  const [quotaUnit, setQuotaUnit] = useState('MB');
  const [savingQuota, setSavingQuota] = useState(false);
  const { logout, enterCompany } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { loadCompanies(); loadDbStats(); }, []);

  const loadCompanies = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`);
      setCompanies(data);
    } catch (err) {
      toast.error('Σφάλμα φόρτωσης εταιριών');
    } finally {
      setLoading(false);
    }
  };

  const loadDbStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/super/db-stats`);
      setDbStats(data);
      // Pre-fill quota input in MB
      if (data.quota_bytes) {
        const mb = data.quota_bytes / 1024 / 1024;
        if (mb >= 1024) { setQuotaInput((mb / 1024).toFixed(2)); setQuotaUnit('GB'); }
        else { setQuotaInput(mb.toFixed(0)); setQuotaUnit('MB'); }
      }
    } catch (err) {
      // silent fail (non-blocking)
    }
  };

  const handleSaveQuota = async () => {
    const val = parseFloat(quotaInput);
    if (isNaN(val) || val < 0) {
      toast.error('Δώστε έγκυρο αριθμό');
      return;
    }
    const bytes = Math.round(val * (quotaUnit === 'GB' ? 1024 * 1024 * 1024 : 1024 * 1024));
    setSavingQuota(true);
    try {
      await axios.put(`${API_URL}/super/storage-quota`, { quota_bytes: bytes });
      toast.success('Το όριο αποθήκευσης ενημερώθηκε');
      loadDbStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Σφάλμα ενημέρωσης ορίου');
    } finally {
      setSavingQuota(false);
    }
  };

  const handleCreate = async () => {
    if (!form.company_name || !form.username || !form.password || !form.user_password) {
      toast.error('Όλα τα πεδία είναι υποχρεωτικά!');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/companies`, form);
      toast.success('Η εταιρία δημιουργήθηκε!');
      setDialogOpen(false);
      setForm({ company_name: '', username: '', password: '', user_password: '' });
      loadCompanies();
      loadDbStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Σφάλμα δημιουργίας');
    } finally {
      setCreating(false);
    }
  };

  const handleEnterCompany = async (companyId) => {
    const ok = await enterCompany(companyId);
    if (ok) {
      navigate('/dashboard');
    } else {
      toast.error('Σφάλμα εισόδου στην εταιρία');
    }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    if (!window.confirm(`Είστε σίγουροι ότι θέλετε να διαγράψετε την "${companyName}" και ΟΛΑ τα δεδομένα της;`)) return;
    try {
      await axios.delete(`${API_URL}/companies/${companyId}`);
      toast.success('Η εταιρία διαγράφηκε');
      loadCompanies();
      loadDbStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Σφάλμα διαγραφής');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-600 rounded-xl">
              <Printer className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">MyPrinterManager.gr</h1>
              <p className="text-blue-300/60 text-sm">Super Admin - Διαχείριση Εταιριών</p>
            </div>
          </div>
          <Button variant="ghost" className="text-white hover:bg-white/10" onClick={logout} data-testid="super-logout">
            <LogOut className="h-5 w-5 mr-2" /> Έξοδος
          </Button>
        </div>

        {/* Storage Usage Section (Super Admin only) */}
        {dbStats && (
          <Card className="bg-white/10 border-white/10 mb-6" data-testid="storage-stats-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center gap-2">
                <Database className="h-5 w-5" />
                ΑΠΟΘΗΚΕΥΤΙΚΟΣ ΧΩΡΟΣ ΒΑΣΗΣ
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Usage bar */}
              <div className="mb-4">
                <div className="flex justify-between items-baseline mb-1">
                  <span className="text-sm text-blue-200/80">
                    <HardDrive className="inline h-4 w-4 mr-1" />
                    Σε χρήση: <span className="font-bold text-white">{fmtBytes(dbStats.total_storage_size_bytes)}</span>
                    {dbStats.quota_bytes > 0 && (
                      <span className="text-blue-300/60"> / {fmtBytes(dbStats.quota_bytes)}</span>
                    )}
                  </span>
                  <span className={`text-lg font-bold ${dbStats.usage_percent > 90 ? 'text-red-400' : dbStats.usage_percent > 70 ? 'text-amber-400' : 'text-emerald-400'}`}>
                    {dbStats.quota_bytes > 0 ? `${dbStats.usage_percent}%` : '— (δεν έχει οριστεί όριο)'}
                  </span>
                </div>
                {dbStats.quota_bytes > 0 && (
                  <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-full transition-all ${dbStats.usage_percent > 90 ? 'bg-red-500' : dbStats.usage_percent > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min(dbStats.usage_percent, 100)}%` }}
                      data-testid="storage-usage-bar"
                    />
                  </div>
                )}
              </div>

              {/* Quota input */}
              <div className="flex flex-col sm:flex-row gap-2 mb-4 p-3 bg-white/5 rounded-lg">
                <div className="flex-1">
                  <label className="text-xs text-blue-300/70 mb-1 block">Όριο cloud (που έχετε αγοράσει)</label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={quotaInput}
                      onChange={(e) => setQuotaInput(e.target.value)}
                      placeholder="π.χ. 500"
                      className="bg-white/10 text-white border-white/20"
                      data-testid="quota-input"
                    />
                    <select
                      value={quotaUnit}
                      onChange={(e) => setQuotaUnit(e.target.value)}
                      className="px-3 py-2 bg-white/10 text-white border border-white/20 rounded-md"
                      data-testid="quota-unit"
                    >
                      <option value="MB">MB</option>
                      <option value="GB">GB</option>
                    </select>
                  </div>
                </div>
                <Button
                  onClick={handleSaveQuota}
                  disabled={savingQuota}
                  className="bg-emerald-600 hover:bg-emerald-700 sm:self-end"
                  data-testid="save-quota-btn"
                >
                  {savingQuota ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Αποθήκευση'}
                </Button>
              </div>

              {/* Quick stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-white/5 p-2 rounded text-center">
                  <div className="text-xs text-blue-300/60">Έγγραφα</div>
                  <div className="text-lg font-bold text-white">{dbStats.total_doc_count.toLocaleString('el-GR')}</div>
                </div>
                <div className="bg-white/5 p-2 rounded text-center">
                  <div className="text-xs text-blue-300/60">Δεδομένα</div>
                  <div className="text-lg font-bold text-white">{fmtBytes(dbStats.total_data_size_bytes)}</div>
                </div>
                <div className="bg-white/5 p-2 rounded text-center">
                  <div className="text-xs text-blue-300/60">Δείκτες</div>
                  <div className="text-lg font-bold text-white">{fmtBytes(dbStats.total_index_size_bytes)}</div>
                </div>
                <div className="bg-white/5 p-2 rounded text-center">
                  <div className="text-xs text-blue-300/60">Συλλογές</div>
                  <div className="text-lg font-bold text-white">{dbStats.collections_count}</div>
                </div>
              </div>

              {/* Per-company breakdown */}
              {dbStats.per_company && dbStats.per_company.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-blue-200/80 mb-2">Ανά Εταιρεία:</h3>
                  <div className="space-y-2" data-testid="per-company-stats">
                    {dbStats.per_company.map((c) => (
                      <div key={c.company_id} className="flex items-center justify-between bg-white/5 px-3 py-2 rounded">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white truncate">{c.company_name}</div>
                          <div className="text-xs text-blue-300/60">{c.doc_count.toLocaleString('el-GR')} έγγραφα</div>
                        </div>
                        <div className="text-right ml-3">
                          <div className="text-sm font-bold text-white">{fmtBytes(c.size_bytes)}</div>
                          <div className="text-xs text-blue-300/60">{c.percent_of_total}% συνόλου</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Create Company Button */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="mb-6 bg-emerald-600 hover:bg-emerald-700" data-testid="create-company-btn">
              <Plus className="h-5 w-5 mr-2" /> Νέα Εταιρία
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Εγκατάσταση Νέας Εταιρίας</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <label className="text-sm font-medium">Όνομα Εταιρίας</label>
                <Input data-testid="new-company-name" placeholder="π.χ. ΠΑΠΑΔΟΠΟΥΛΟΣ Α.Ε." value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium">Username Εισόδου</label>
                <Input data-testid="new-company-username" placeholder="Username εταιρίας" value={form.username} onChange={e => setForm({...form, username: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium">Password Εισόδου</label>
                <Input data-testid="new-company-password" type="password" placeholder="Password εταιρίας" value={form.password} onChange={e => setForm({...form, password: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium">Κωδικός Χειριστή (2ος κωδικός)</label>
                <Input data-testid="new-company-user-password" type="password" placeholder="Κωδικός χειριστή" value={form.user_password} onChange={e => setForm({...form, user_password: e.target.value})} />
              </div>
              <Button data-testid="submit-create-company" className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleCreate} disabled={creating}>
                {creating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Δημιουργία...</> : 'Δημιουργία Εταιρίας'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Company List */}
        {loading ? (
          <div className="text-center text-white py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>
        ) : companies.length === 0 ? (
          <Card className="bg-white/10 border-white/10">
            <CardContent className="py-12 text-center text-white/60">
              Δεν υπάρχουν εταιρίες. Δημιουργήστε μια νέα!
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {companies.map(company => (
              <Card key={company.id} className="bg-white/10 border-white/10 hover:bg-white/15 transition-colors" data-testid={`company-card-${company.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-600/30 rounded-lg">
                      <Building2 className="h-6 w-6 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg text-white truncate">{company.company_name}</CardTitle>
                      <p className="text-sm text-blue-300/60">@{company.username}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 text-sm text-blue-200/60 mb-4">
                    <span className="flex items-center gap-1"><Users className="h-4 w-4" /> {company.customer_count || 0} πελάτες</span>
                    <span className="flex items-center gap-1"><Printer className="h-4 w-4" /> {company.machine_count || 0} μηχ/τα</span>
                  </div>
                  <div className="flex gap-2">
                    <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={() => handleEnterCompany(company.id)} data-testid={`enter-company-${company.id}`}>
                      <LogIn className="h-4 w-4 mr-2" /> Είσοδος
                    </Button>
                    <Button variant="ghost" size="icon" className="text-red-400 hover:bg-red-500/20 hover:text-red-300" onClick={() => handleDeleteCompany(company.id, company.company_name)} data-testid={`delete-company-${company.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyManagerPage;
